/**
 * @description Branding API service for fetching tenant branding config
 *
 * Key features:
 * - Fetches tenant branding from public endpoint (no auth required)
 * - Fetches platform config branding for LynxPay defaults (no auth required)
 * - Uses plain fetch instead of apiClient (runs before auth is established)
 * - Graceful 404/error handling (returns null on failure)
 *
 * @dependencies
 * - None (uses native fetch, not apiClient)
 *
 * @notes
 * - Called by useTenantBranding hook during app initialization
 * - Public endpoints have 5-minute Cache-Control headers
 * - Does not use apiClient because branding loads before auth context
 * - GET /api/public/tenants/:slug/branding is occupancy (SO then CP). Payload
 *   includes signupEnabled (LYN-320) and occupancy kind + connected vanity
 *   (LYN-329). Platform config is LynxPay-only.
 */

import { getTenantBackendBaseUrl } from '../../utils/lynxBuildHostname';

function resolveBrandingBackendUrl() {
  return getTenantBackendBaseUrl();
}

/**
 * Fetch tenant branding by slug
 * @param {string} slug - Tenant URL slug (e.g., 'acme')
 * @returns {Promise<object|null>} Branding config or null if not found
 */
export async function fetchTenantBranding(slug) {
  try {
    const response = await fetch(`${resolveBrandingBackendUrl()}/api/public/tenants/${slug}/branding`);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Branding fetch failed: ${response.status}`);
    }

    const data = await response.json();

    if (data.success && data.data) {
      return data.data;
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch tenant branding:', error.message);
    return null;
  }
}

/**
 * Fetch platform config branding (LynxPay defaults from DB)
 * Used when no tenant slug is detected (localhost, app.lynxpay.us)
 * @returns {Promise<object|null>} { branding } or null on failure
 */
export async function fetchPlatformConfig() {
  try {
    const response = await fetch(`${resolveBrandingBackendUrl()}/api/public/platform-config`);

    if (!response.ok) {
      throw new Error(`Platform config fetch failed: ${response.status}`);
    }

    const data = await response.json();

    if (data.success && data.data) {
      return data.data;
    }

    return null;
  } catch (error) {
    console.error('Failed to fetch platform config:', error.message);
    return null;
  }
}
